import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { client } from '@php-beam/packages/frontend/src/api/client';
import { LeadEmployeeInfo } from './types';

export default function EmployeeSection({
  leadId,
  employees,
  defaults,
  onDone,
}: {
  leadId: number;
  employees: LeadEmployeeInfo[];
  defaults: Record<string, any>;
  onDone: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(defaults);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const simulate = async () => {
    setSubmitting(true);
    try {
      await client.post(`/devtools/documents/lead/${leadId}/employee`, formData);
      toast({
        title: 'Employee created & application simulated',
        status: 'success',
        duration: 3000,
      });
      onDone();
    } catch (err: any) {
      toast({
        title: 'Failed to simulate employee',
        description: err?.response?.data?.message || 'Unknown error',
        status: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={3} borderWidth={1} borderRadius="md" borderColor="gray.200">
      <HStack mb={2}>
        <Text fontWeight="semibold" fontSize="sm">
          Employee Application
        </Text>
        {employees.length > 0 && (
          <Badge colorScheme="green" fontSize="xs">
            {employees.length} employee(s)
          </Badge>
        )}
      </HStack>

      <HStack spacing={2} mb={2}>
        <Button
          size="sm"
          colorScheme="purple"
          onClick={simulate}
          isLoading={submitting}
        >
          Quick Add Employee
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Hide' : 'Edit'}
        </Button>
      </HStack>

      {showForm && (
        <EmployeeForm data={formData} onChange={setFormData} />
      )}

      {employees.length > 0 && (
        <Box mt={2}>
          {employees.map((emp) => (
            <HStack key={emp.id} fontSize="xs" py={0.5}>
              <Badge fontSize="2xs" colorScheme={emp.lead_document_id ? 'green' : 'gray'}>
                {emp.lead_document_id ? 'signed' : 'pending'}
              </Badge>
              <Text>
                #{emp.id} — {emp.first_name} {emp.last_name}
              </Text>
            </HStack>
          ))}
        </Box>
      )}
    </Box>
  );
}

function EmployeeForm({
  data,
  onChange,
}: {
  data: Record<string, any>;
  onChange: (d: Record<string, any>) => void;
}) {
  const set = (key: string, value: any) => onChange({ ...data, [key]: value });

  return (
    <SimpleGrid columns={2} spacing={2} mt={2}>
      <FormControl>
        <FormLabel fontSize="xs">First Name</FormLabel>
        <Input size="sm" value={data.first_name ?? ''} onChange={(e) => set('first_name', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Last Name</FormLabel>
        <Input size="sm" value={data.last_name ?? ''} onChange={(e) => set('last_name', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Email</FormLabel>
        <Input size="sm" value={data.email ?? ''} onChange={(e) => set('email', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Phone</FormLabel>
        <Input size="sm" value={data.phone ?? ''} onChange={(e) => set('phone', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Pay Type</FormLabel>
        <Select size="sm" value={data.pay_type ?? ''} onChange={(e) => set('pay_type', e.target.value)}>
          <option value="hourly">Hourly</option>
          <option value="hourly tipped">Hourly Tipped</option>
          <option value="salary">Salary</option>
          <option value="commission">Commission</option>
          <option value="piecework">Piecework</option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Pay Rate</FormLabel>
        <Input size="sm" type="number" value={data.pay_rate ?? ''} onChange={(e) => set('pay_rate', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Pay Cycle</FormLabel>
        <Select size="sm" value={data.pay_cycle ?? ''} onChange={(e) => set('pay_cycle', e.target.value)}>
          <option value="weekly">Weekly</option>
          <option value="biweekly">Biweekly</option>
          <option value="semimonthly">Semimonthly</option>
          <option value="monthly">Monthly</option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">WC Code</FormLabel>
        <Input size="sm" value={data.wc_code ?? ''} onChange={(e) => set('wc_code', e.target.value)} />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Employment Status</FormLabel>
        <Select size="sm" value={data.employment_status ?? ''} onChange={(e) => set('employment_status', e.target.value)}>
          <option value="full_time">Full Time</option>
          <option value="part_time">Part Time</option>
          <option value="seasonal">Seasonal</option>
          <option value="contractor">Contractor</option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs">Direct Deposit</FormLabel>
        <Select size="sm" value={data.direct_deposit ?? ''} onChange={(e) => set('direct_deposit', e.target.value)}>
          <option value="required">Required</option>
          <option value="optional">Optional</option>
          <option value="net_checks">Net Checks</option>
        </Select>
      </FormControl>
    </SimpleGrid>
  );
}
